import { useDebouncedValue } from '@mantine/hooks';
import { trpc } from '@utils/trpc';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import type { ChangeEvent } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { LoadingScreen } from '~/components/LoadingScreen';
import { NoDataDisplay } from '~/components/NoDataDisplay';
import { classNames } from '~/components/utils';
import { FAIcon } from '~/components/utils/FAIcons';
import { jobCategoryPath } from '~/components/pages/jobs/businessCategoryTabs';
import { useUserContext } from '~/react_context/UserContext';
import { useNavStore } from '~/stores/nav.store';
import JobSearchResultCard from './JobSearchResultCard';

interface Props {
	lightMode?: boolean;
}

export const SidebarJobSearchComboBox = ({ lightMode }: Props) => {
	const {
		visibility: { categoryOptions, getJobCategoryIcon, getJobCategoryLabel },
	} = useUserContext();
	const { collapsedNav: isCollapsed, setCollapsedNav: setIsCollapsed } = useNavStore();

	const router = useRouter();

	const [query, setQuery] = useState<string>('');
	const [categorySearch, setCategorySearch] = useState<string>(query ?? '');

	const queryInput = useRef<HTMLInputElement>(null);
	const menuContainer = useRef<HTMLDivElement>(null);

	const isFocusedSearch = !!query && query === categorySearch;

	//Is the combobox open
	const [isOpen, setIsOpen] = useState<boolean>(false);

	useEffect(() => setIsOpen(false), [router.asPath]);

	//The current job id that is focused via keyboard navigation
	const [keyFocusedId, setKeyFocusedId] = useState<number | undefined>(undefined);

	useEffect(() => {
		setIsOpen(!!query);
	}, [query]);

	const [debouncedQuery] = useDebouncedValue(query, 500);
	const isQueryDelayed = query !== debouncedQuery;
	const backendSearchQuery = trpc.jobs.globalJobSearch.useQuery(
		{
			searchQuery: debouncedQuery,
		},
		{
			enabled: !isCollapsed && debouncedQuery.trim().length > 0,
		}
	);
	const businessCategoriesWithJobs = backendSearchQuery.data ?? [];

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		setQuery(e.target.value);
	};

	const handleJobClick = (jobId: number) => {
		router.push(`/jobs/${jobId}`);
	};

	const handleQuickEstimateClick = (id: number) => {
		router.push(`/quick-estimate/view/${id}`);
	};

	const handleViewAllResultsClick = (category?: string) => {
		setIsOpen(false);
		if (category && category !== 'QUICK_ESTIMATES') {
			router.push({ pathname: jobCategoryPath(category, categoryOptions), query: { search: query } });
			setCategorySearch(query);
		} else {
			router.push(`/quick-estimate?search=${query}`);
		}
	};

	const allJobIds = businessCategoriesWithJobs.flatMap((group) => group.jobs.map((job) => job.id));
	const handleQueryKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key == 'Enter') {
			e.preventDefault();
			if (keyFocusedId) {
				handleJobClick(keyFocusedId);
			}
		}
		if (e.key == 'ArrowDown') {
			if (allJobIds.length) {
				e.preventDefault();
				if (!keyFocusedId) {
					setKeyFocusedId(allJobIds[0]);
				} else {
					const currentJobIdx = allJobIds.findIndex((jobId) => jobId === keyFocusedId);
					if (currentJobIdx < allJobIds.length - 1) {
						setKeyFocusedId(allJobIds[currentJobIdx + 1]);
					}
				}
			}
		}
		if (e.key == 'ArrowUp') {
			if (allJobIds.length) {
				e.preventDefault();
				if (!keyFocusedId) {
					setKeyFocusedId(allJobIds[allJobIds.length - 1]);
				} else {
					const currentJobIdx = allJobIds.findIndex((jobId) => jobId === keyFocusedId);
					if (currentJobIdx > 0) {
						setKeyFocusedId(allJobIds[currentJobIdx - 1]);
					}
				}
			}
		}
	};

	useEffect(() => {
		function handleClickOutside(event: MouseEvent) {
			const target = event.target;
			if (!(target instanceof Node)) return;
			if (menuContainer.current && !menuContainer.current.contains(target) && queryInput.current !== target) {
				setIsOpen(false);
				setQuery('');
			} else if (queryInput.current === target && !!query) {
				setIsOpen(true);
			}
		}

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [menuContainer]);

	return isCollapsed ? (
		<div
			className={clsx('w-10 h-10 flex justify-center items-center shadow-xs rounded-md p-4', {
				'border border-gray-400 bg-white': lightMode,
				'border border-gray-700 bg-gray-700': !lightMode,
			})}
			onClick={() => setIsCollapsed && setIsCollapsed(false)}
		>
			<FAIcon icon="magnifying-glass" className="text-gray-400" />
		</div>
	) : (
		<div className="w-full">
			<div className="relative whitespace-nowrap w-full flex">
				<input
					role="combobox"
					id="job-search"
					aria-controls="job-search-dropdown"
					aria-expanded={isOpen}
					type="text"
					ref={queryInput}
					placeholder="Search All Jobs"
					value={query}
					className={clsx(
						'w-full inline rounded-md shadow-xs bg-gray-700 py-2 pl-3 pr-5 placeholder-gray-400 focus:border-blue-500 focus:outline-hidden focus:ring-blue-500 sm:text-sm ',
						{
							'border-2 border-primary': isFocusedSearch,
							'bg-white text-gray-900 placeholder-gray-500 border border-gray-400': lightMode,
							'text-white border border-gray-700': !lightMode,
						}
					)}
					onKeyDown={(e) => {
						if (e.key == 'Enter') {
							e.preventDefault();
						}
					}}
					onKeyUp={handleQueryKeyPress}
					onChange={handleInputChange}
					autoComplete="off"
				/>
				<div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
					<FAIcon
						icon={isOpen ? 'chevron-down' : isFocusedSearch ? 'eye' : 'magnifying-glass'}
						className={`${isFocusedSearch ? 'text-primary' : 'text-gray-400'}`}
					/>
				</div>
			</div>

			<div className="relative w-full pt-0.5">
				<div
					id="job-search-dropdown"
					className={classNames(
						'absolute bg-white border-2 border-gray-200 rounded-md shadow-md z-1000 w-[456px] overflow-y-auto',
						!isOpen && 'hidden'
					)}
					ref={menuContainer}
				>
					{backendSearchQuery.isLoading || isQueryDelayed ? (
						<div className="my-5">
							<LoadingScreen header="Searching Your Jobs" subheader={`Hang Tight! We're searching all of your jobs.`} />
						</div>
					) : backendSearchQuery.isSuccess && !businessCategoriesWithJobs.length ? (
						<div className="my-5">
							<NoDataDisplay
								icon="briefcase"
								header="No Jobs Found"
								subheader={`Sorry, we couldn't find any jobs matching "${query}"`}
								actionButtonText="Clear Search"
								actionButtonOnClick={() => setQuery('')}
							/>
						</div>
					) : (
						backendSearchQuery.isSuccess &&
						!!businessCategoriesWithJobs.length &&
						businessCategoriesWithJobs.map(({ category, jobs, totalInCategory }) => {
							const { icon, color: rawColor } = getJobCategoryIcon(category);
							const showViewAll = jobs.length < totalInCategory;
							const categoryLabel = getJobCategoryLabel(category);
							const categoryForRoute = category === 'QUICK_ESTIMATES' ? undefined : category;

							return (
								<div key={category}>
									<div className="flex w-full items-center justify-between bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-700">
										<div className="flex items-center gap-1.5">
											<FAIcon prefix="fad" icon={icon} style={{ color: rawColor }} />
											<h3>{categoryLabel.toUpperCase()}</h3>
										</div>
										{showViewAll && (
											<button
												type="button"
												onClick={() => handleViewAllResultsClick(categoryForRoute)}
												className="text-primary hover:underline"
											>
												View All {totalInCategory}
											</button>
										)}
									</div>
									{jobs.map((job) => (
										<JobSearchResultCard
											key={job.id}
											job={job}
											query={query}
											isFocused={keyFocusedId === job.id}
											onClick={() => (job.businessCategory ? handleJobClick(job.id) : handleQuickEstimateClick(job.id))}
										/>
									))}
									{showViewAll && (
										<button
											type="button"
											onClick={() => handleViewAllResultsClick(categoryForRoute)}
											className="flex w-full items-center gap-2 px-3 py-2 text-xs text-gray-500 hover:bg-glazier-blue-50"
										>
											<FAIcon icon="magnifying-glass" />
											<span>
												See all {categoryLabel.toLowerCase()} matching &quot;{query}&quot;
											</span>
										</button>
									)}
								</div>
							);
						})
					)}
				</div>
			</div>
		</div>
	);
};
